import { v4 as uuidv4 } from 'uuid';
import { CardState, DeckState, Position, Rotation } from '../types/game';
import { DEFAULT_CARD_BACK } from '../components/card/CardUtils';

// Interface for the tableDeck format
interface TableDeckData {
  tableDeck: {
    name: string;
    back_image_url: string;
    cards: Array<{
      front_image_url: string;
      name?: string;
    }>;
  };
}

interface CompressedDeckData {
  t?: {
    n?: string;
    b?: string;
    c?: Array<{ f: string; n?: string }>;
  };
}

// Metadata for available sample decks
export interface SampleDeckMetadata {
  name: string;
  filename: string;
  coverImageUrl?: string;
}

// Default position and rotation for new decks
const DEFAULT_POSITION: Position = { x: 0, y: 0 };
const DEFAULT_ROTATION: Rotation = 0;

/**
 * Fetches available sample decks metadata
 */
export async function fetchSampleDecksMetadata(): Promise<SampleDeckMetadata[]> {
  try {
    const response = await fetch('/sample-decks-metadata.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch sample decks metadata: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching sample decks metadata:', error);
    return [];
  }
}

/**
 * Parses a JSON string into a deck state
 * Only supports the tableDeck format
 */
export function importDeckFromJson(jsonText: string, position: Position): DeckState {
  try {
    const parsed = JSON.parse(jsonText);
    
    // Check if it's the tableDeck format
    if (parsed.tableDeck && parsed.tableDeck.cards) {
      return importTableDeckFormat(parsed as TableDeckData, position);
    }
    
    // If not tableDeck format, throw error
    throw new Error('Deck JSON must be in tableDeck format. Expected: { "tableDeck": { "name": "...", "back_image_url": "...", "cards": [...] } }');
  } catch (error) {
    console.error("Error parsing deck JSON:", error);
    throw new Error(`Failed to parse deck JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Imports deck from the tableDeck format
 */
function importTableDeckFormat(tableDeckData: TableDeckData, position: Position): DeckState {
  const { tableDeck } = tableDeckData;
  
  if (!tableDeck.cards || !Array.isArray(tableDeck.cards)) {
    throw new Error('tableDeck must contain a cards array');
  }
  
  if (tableDeck.cards.length === 0) {
    throw new Error('Deck cannot be empty');
  }
  
  // Validate each card has a front image URL
  tableDeck.cards.forEach((card, index) => {
    if (!card.front_image_url) {
      throw new Error(`Card at position ${index} is missing front_image_url`);
    }
  });
  
  // Convert cards to CardState objects using the shared back image
  const cards: CardState[] = tableDeck.cards.map((card, index) => ({
    id: uuidv4(),
    position: position,
    rotation: DEFAULT_ROTATION,
    isFaceUp: false,
    imageUrl: card.front_image_url,
    backImageUrl: tableDeck.back_image_url || DEFAULT_CARD_BACK,
    name: card.name || `Card ${index + 1}`,
    counters: {}
  }));
  
  // Create a DeckState
  return {
    id: uuidv4(),
    name: tableDeck.name || 'Imported Deck',
    position,
    cards,
    rotation: DEFAULT_ROTATION,
    faceUp: false,
    collapsed: false
  };
}

/**
 * Fetches a sample deck by filename and converts it to a DeckState
 */
export async function fetchSampleDeck(filename: string): Promise<DeckState | null> {
  try {
    const response = await fetch(`/sample-decks/${filename}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch deck: ${response.status}`);
    }
    
    const rawDeckJson = await response.text();
    return importDeckFromJson(rawDeckJson, DEFAULT_POSITION);
  } catch (error) {
    console.error(`Error fetching sample deck ${filename}:`, error);
    return null;
  }
}

/**
 * Decodes a base64 encoded deck and converts it to a DeckState
 * This is used for loading decks from URL parameters
 */
export function importDeckFromBase64(base64Data: string, position: Position): DeckState {
  try {
    // Make base64 URL-safe by replacing - and _ with + and /
    const normalizedBase64 = base64Data.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const paddedBase64 = normalizedBase64 + '='.repeat((4 - normalizedBase64.length % 4) % 4);
    
    // Decode base64 to string
    const jsonString = atob(paddedBase64);
    
    // Parse the JSON and create deck state
    return importDeckFromJson(jsonString, position);
  } catch (error) {
    console.error("Error decoding base64 deck:", error);
    throw new Error(`Failed to decode base64 deck: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Encodes a deck to base64 for URL sharing
 * Uses the tableDeck format
 */
export function encodeDeckToBase64(deck: DeckState): string {
  try {
    // Use the tableDeck format
    const tableDeckData = {
      tableDeck: {
        name: deck.name,
        back_image_url: deck.cards[0]?.backImageUrl || DEFAULT_CARD_BACK,
        cards: deck.cards.map(card => ({
          front_image_url: card.imageUrl,
          name: card.name
        }))
      }
    };
    
    const jsonString = JSON.stringify(tableDeckData);
    
    // Encode to base64
    const base64 = btoa(jsonString);
    
    // Make URL-safe by replacing + and / with - and _
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error("Error encoding deck to base64:", error);
    throw new Error(`Failed to encode deck to base64: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Compresses a deck by removing redundant data and shortening URLs
 * Uses the tableDeck format
 */
export function compressDeckForUrl(deck: DeckState): string {
  try {
    // Use compressed tableDeck format
    const compressedCards = deck.cards.map(card => ({
      f: card.imageUrl, // front_image_url
      n: card.name      // name
    }));
    
    const compressedTableDeckData = {
      t: { // tableDeck
        n: deck.name, // name
        b: deck.cards[0]?.backImageUrl || DEFAULT_CARD_BACK, // back_image_url
        c: compressedCards // cards
      }
    };
    
    const jsonString = JSON.stringify(compressedTableDeckData);
    
    // Encode to base64 with URL-safe characters
    const base64 = btoa(jsonString);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error("Error compressing deck:", error);
    throw new Error(`Failed to compress deck: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Decompresses a deck from the compressed URL format
 * Only supports tableDeck compressed format
 */
export function decompressDeckFromUrl(compressedData: string, position: Position): DeckState {
  try {
    // Make base64 URL-safe by replacing - and _ with + and /
    const normalizedBase64 = compressedData.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const paddedBase64 = normalizedBase64 + '='.repeat((4 - normalizedBase64.length % 4) % 4);
    
    // Decode base64 to string
    const jsonString = atob(paddedBase64);
    
    // Parse the compressed data
    const data = JSON.parse(jsonString) as CompressedDeckData;
    
    // Check if it's the tableDeck compressed format
    if (data.t?.c) {
      // tableDeck compressed format
      const tableDeckData = {
        tableDeck: {
          name: data.t.n ?? 'Imported Deck',
          back_image_url: data.t.b ?? DEFAULT_CARD_BACK,
          cards: data.t.c.map(card => ({
            front_image_url: card.f,
            name: card.n
          }))
        }
      };
      
      return importDeckFromJson(JSON.stringify(tableDeckData), position);
    }
    
    // If not tableDeck format, throw error
    throw new Error('Compressed deck must be in tableDeck format');
  } catch (error) {
    console.error("Error decompressing deck:", error);
    throw new Error(`Failed to decompress deck: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
} 
